import { useGetList, useTimeout } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";

import type { Contact, ContactNote } from "@/components/atomic-crm/types";
import { DashboardStepper } from "@/components/atomic-crm/dashboard/DashboardStepper";
import { DashboardActivityLog } from "@/components/atomic-crm/dashboard/DashboardActivityLog";
import { Welcome } from "@/components/atomic-crm/dashboard/Welcome";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { KpiCardsMobile } from "./KpiCards";
import { useBldrOsStats } from "./useBldrOsStats";
import { BldrOsOnboarding } from "../onboarding/BldrOsOnboarding";
import { useOnboardingStatus } from "../onboarding/useOnboardingStatus";
import { QuickAddLeadButton } from "../contacts/QuickAddLeadButton";

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  return (
    <>
      <MobileHeader>
        <div className="flex items-center gap-2 text-secondary-foreground no-underline py-3">
          <img
            className="[.light_&]:hidden h-6"
            src={darkModeLogo}
            alt={title}
          />
          <img
            className="[.dark_&]:hidden h-6"
            src={lightModeLogo}
            alt={title}
          />
          <h1 className="text-xl font-semibold">{title}</h1>
        </div>
      </MobileHeader>
      <MobileContent>{children}</MobileContent>
    </>
  );
};

const Loading = () => (
  <Wrapper>
    <Skeleton className="h-4 w-3/4 mb-4" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
  </Wrapper>
);

export const MobileBldrOsDashboard = () => {
  const { needsOnboarding, isPending: onboardingPending } =
    useOnboardingStatus();
  const stats = useBldrOsStats();

  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });
  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });
  const oneSecondHasPassed = useTimeout(1000);

  const isPending =
    isPendingContact || isPendingContactNotes || onboardingPending;

  if (isPending) {
    return oneSecondHasPassed ? <Loading /> : null;
  }

  if (needsOnboarding) {
    return (
      <Wrapper>
        <BldrOsOnboarding />
      </Wrapper>
    );
  }

  if (!totalContact) {
    return (
      <Wrapper>
        <DashboardStepper step={1} />
      </Wrapper>
    );
  }

  if (!totalContactNotes) {
    return (
      <Wrapper>
        <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {!stats.isPending && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold">Dashboard</h2>
            <QuickAddLeadButton />
          </div>
          <KpiCardsMobile stats={stats} />
        </>
      )}
      <div className="grid grid-cols-1 gap-6 mt-1">
        {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
        <DashboardActivityLog />
      </div>
    </Wrapper>
  );
};
